import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, TimeoutError, throwError } from 'rxjs';
import { catchError, map, timeout } from 'rxjs/operators';
import { environment } from '@env/environment';
import { SequencingRunRepository } from '@service/sequencing/sequencing-run-repository';
import { CreateSequencingRunRequest } from '@model/sequencing/create-sequencing-run-request.model';
import {
  CreateSequencingRunResponse,
  SequencingRunStatus
} from '@model/sequencing/create-sequencing-run-response.model';
import { SequencingRunDetail } from '@model/sequencing/sequencing-run-detail.model';
import { RunLibraryAssignmentFactory } from './run-library-assignment.factory';
import {
  SequencingRunListParams,
  SequencingRunListResponse,
  SequencingRunListItem
} from '@model/sequencing/sequencing-run-list.model';
import { UpdateSequencingRunRequest } from '@model/sequencing/update-sequencing-run-request.model';
import { UpdateSequencingRunResponse } from '@model/sequencing/update-sequencing-run-response.model';
import {
  MarkRunAsFailedRequest,
  MarkRunAsFailedResponse
} from '@model/sequencing/mark-run-as-failed.model';
import {
  RunSpecimen,
  MarkRunAsSuccessRequest,
  MarkRunAsSuccessResponse
} from '@model/sequencing/mark-run-as-success.model';
import { SendToAnalysisResponse } from '@model/sequencing/send-to-analysis.model';
import {
  CreateSequencingRunBackendRequest,
  CreateSequencingRunBackendResponse,
  MarkRunAsFailedBackendRequest,
  MarkRunAsFailedBackendResponse,
  MarkRunAsSuccessBackendRequest,
  MarkRunAsSuccessBackendResponse,
  RunSpecimenBackendDto,
  SequencingRunDetailBackendDto,
  SequencingRunListBackendResponse,
  SequencingRunListItemBackendDto,
  UpdateSequencingRunBackendRequest,
  UpdateSequencingRunBackendResponse
} from './sequencing-run-backend.dto';

/**
 * HTTP implementation of SequencingRunRepository.
 * Connects to the backend API for sequencing run operations.
 *
 * This adapter follows the Dependency Inversion Principle:
 * - Domain layer defines the interface (SequencingRunRepository)
 * - Infrastructure layer provides the implementation (HttpSequencingRunRepository)
 */
@Injectable({
  providedIn: 'root'
})
export class HttpSequencingRunRepository extends SequencingRunRepository {
  private readonly httpClient = inject(HttpClient);
  private readonly baseUrl = `${environment.laboratory.baseUrl}${environment.laboratory.context}`;
  private readonly sequenci = '/sequencing-runs';
  /** HTTP timeout for requests in milliseconds */
  private readonly HTTP_TIMEOUT_MS = 30000;

  /**
   * Create a new sequencing run with library assignments.
   * Endpoint: POST /api/laboratorio/sequencing-runs
   *
   * @param request The run parameters and library assignments
   * @returns Observable of the created run response
   * @throws TimeoutError if the request exceeds HTTP_TIMEOUT_MS
   */
  createSequencingRun(
    request: CreateSequencingRunRequest
  ): Observable<CreateSequencingRunResponse> {
    const backendRequest = this.mapToBackendRequest(request);

    return this.httpClient
      .post<CreateSequencingRunBackendResponse>(
        `${this.baseUrl}${this.sequenci}`,
        backendRequest
      )
      .pipe(
        timeout(this.HTTP_TIMEOUT_MS),
        map((response) => this.mapToResponse(response)),
        catchError((error: unknown) => {
          if (error instanceof TimeoutError) {
            return throwError(
              () =>
                new Error(
                  'La solicitud ha excedido el tiempo de espera. Por favor, intente nuevamente.'
                )
            );
          }
          return throwError(() => error as Error);
        })
      );
  }

  /**
   * Map domain request to backend DTO
   */
  private mapToBackendRequest(
    request: CreateSequencingRunRequest
  ): CreateSequencingRunBackendRequest {
    return {
      technology: request.technology,
      equipment: request.equipment,
      version: request.version,
      indexType: request.indexType,
      cellIdentifier: request.cellIdentifier,
      cellLength: request.cellLength,
      cellFilter: request.cellFilter,
      libraryAssignments: request.libraryAssignments.map((assignment) => ({
        libraryCode: assignment.libraryCode,
        replicaColumn: assignment.replicaColumn,
        lineNumber: assignment.lineNumber
      })),
      // Microarray-specific field: custom run name (optional)
      runName: request.runName ?? null
    };
  }

  /**
   * Map backend response to domain model
   */
  private mapToResponse(
    response: CreateSequencingRunBackendResponse
  ): CreateSequencingRunResponse {
    return {
      runId: response.runId,
      runCode: response.runCode,
      equipment: response.equipment,
      version: response.version,
      indexType: response.indexType,
      cellIdentifier: response.cellIdentifier,
      cellLength: response.cellLength,
      cellFilter: response.cellFilter,
      status: response.status as SequencingRunStatus,
      libraryCount: response.libraryCount,
      createdAt: response.createdAt
    };
  }

  /**
   * Get paginated list of sequencing runs with optional filters.
   * Endpoint: GET /api/laboratorio/sequencing-runs
   *
   * @param params Query parameters for pagination, filtering, and sorting
   * @returns Observable of paginated runs response
   */
  getSequencingRuns(
    params: SequencingRunListParams
  ): Observable<SequencingRunListResponse> {
    let httpParams = new HttpParams()
      .set('page', params.page.toString())
      .set('size', params.size.toString());

    if (params.technology) {
      httpParams = httpParams.set('technology', params.technology);
    }

    if (params.status) {
      httpParams = httpParams.set('status', params.status);
    }

    if (params.search && params.search.trim() !== '') {
      httpParams = httpParams.set('search', params.search.trim());
    }

    if (params.searchField) {
      httpParams = httpParams.set('searchField', params.searchField);
    }

    if (params.sortBy) {
      httpParams = httpParams.set('sortBy', params.sortBy);
      httpParams = httpParams.set(
        'sortDirection',
        params.sortDirection ?? 'DESC'
      );
    }

    return this.httpClient
      .get<SequencingRunListBackendResponse>(
        `${this.baseUrl}${this.sequenci}`,
        {
          params: httpParams
        }
      )
      .pipe(map((response) => this.mapToListResponse(response)));
  }

  /**
   * Delete a sequencing run by its ID.
   * Endpoint: DELETE /api/laboratorio/sequencing-runs/{id}
   *
   * @param runId The unique identifier of the run to delete
   * @returns Observable of void
   */
  deleteSequencingRun(runId: string): Observable<void> {
    return this.httpClient.delete<void>(
      `${this.baseUrl}${this.sequenci}/${runId}`
    );
  }

  /**
   * Partially update an existing sequencing run (PATCH semantics).
   * Endpoint: PATCH /api/laboratorio/sequencing-runs/{id}
   *
   * Only fields present in the request are sent to backend.
   * Fields set to undefined are omitted from JSON, backend preserves existing values.
   * Library assignments are always replaced entirely if provided.
   *
   * @param runId The unique identifier of the run to update
   * @param request The updated run parameters (only dirty fields)
   * @returns Observable of the updated run response
   */
  updateSequencingRun(
    runId: string,
    request: UpdateSequencingRunRequest
  ): Observable<UpdateSequencingRunResponse> {
    const backendRequest = this.mapToUpdateBackendRequest(request);

    return this.httpClient
      .patch<UpdateSequencingRunBackendResponse>(
        `${this.baseUrl}${this.sequenci}/${runId}`,
        backendRequest
      )
      .pipe(map((response) => this.mapToUpdateResponse(response)));
  }

  /**
   * Map domain update request to backend DTO for PATCH parcial.
   * Solo incluye campos que tienen valor definido (no undefined).
   * Esto permite enviar solo los campos modificados al backend.
   *
   * MODOS DE ACTUALIZACIÓN DE LIBRERÍAS:
   * - libraryAssignments: Modo reemplazo completo
   * - addedLibraryAssignments + removedLibraryCodes: Modo PATCH incremental
   */
  private mapToUpdateBackendRequest(
    request: UpdateSequencingRunRequest
  ): UpdateSequencingRunBackendRequest {
    const backendRequest: UpdateSequencingRunBackendRequest = {};

    // Solo incluir campos que fueron modificados (tienen valor definido)
    if (request.technology !== undefined) {
      backendRequest.technology = request.technology;
    }
    if (request.equipment !== undefined) {
      backendRequest.equipment = request.equipment;
    }
    if (request.version !== undefined) {
      backendRequest.version = request.version;
    }
    if (request.indexType !== undefined) {
      backendRequest.indexType = request.indexType;
    }
    if (request.cellIdentifier !== undefined) {
      backendRequest.cellIdentifier = request.cellIdentifier;
    }
    if (request.cellLength !== undefined) {
      backendRequest.cellLength = request.cellLength;
    }
    if (request.cellFilter !== undefined) {
      backendRequest.cellFilter = request.cellFilter;
    }

    // Modo reemplazo completo de librerías
    if (request.libraryAssignments !== undefined) {
      backendRequest.libraryAssignments = request.libraryAssignments.map(
        (assignment) => ({
          libraryCode: assignment.libraryCode,
          replicaColumn: assignment.replicaColumn,
          lineNumber: assignment.lineNumber
        })
      );
    }

    // Modo PATCH incremental de librerías
    if (request.addedLibraryAssignments !== undefined) {
      backendRequest.addedLibraryAssignments =
        request.addedLibraryAssignments.map((assignment) => ({
          libraryCode: assignment.libraryCode,
          replicaColumn: assignment.replicaColumn,
          lineNumber: assignment.lineNumber
        }));
    }
    if (request.removedLibraryCodes !== undefined) {
      backendRequest.removedLibraryCodes = request.removedLibraryCodes;
    }

    // Microarray-specific field: custom run name (puede ser null explícito)
    if (request.runName !== undefined) {
      backendRequest.runName = request.runName ?? null;
    }

    return backendRequest;
  }

  /**
   * Map backend update response to domain model
   */
  private mapToUpdateResponse(
    response: UpdateSequencingRunBackendResponse
  ): UpdateSequencingRunResponse {
    return {
      runId: response.runId,
      runCode: response.runCode,
      equipment: response.equipment,
      version: response.version,
      indexType: response.indexType,
      cellIdentifier: response.cellIdentifier,
      cellLength: response.cellLength,
      cellFilter: response.cellFilter,
      status: response.status as SequencingRunStatus,
      libraryCount: response.libraryCount,
      updatedAt: response.updatedAt
    };
  }

  /**
   * Get a sequencing run by its ID with all library assignments.
   * Endpoint: GET /api/laboratorio/sequencing-runs/{id}
   *
   * @param runId The unique identifier of the run
   * @returns Observable of the run detail
   */
  getSequencingRunById(runId: string): Observable<SequencingRunDetail> {
    return this.httpClient
      .get<SequencingRunDetailBackendDto>(
        `${this.baseUrl}${this.sequenci}/${runId}`
      )
      .pipe(map((response) => this.mapToDetailModel(response)));
  }

  /**
   * Generate and download SampleSheet for a sequencing run.
   * Endpoint: GET /api/laboratorio/sequencing-runs/{id}/samplesheet
   *
   * @param runId The unique identifier of the run
   * @returns Observable of the SampleSheet file as Blob
   */
  generateSampleSheet(runId: string): Observable<Blob> {
    return this.httpClient.get(
      `${this.baseUrl}${this.sequenci}/${runId}/samplesheet`,
      {
        responseType: 'blob'
      }
    );
  }

  /**
   * Map backend detail response to domain model.
   * Uses RunLibraryAssignmentFactory to create technology-specific instances.
   */
  private mapToDetailModel(
    response: SequencingRunDetailBackendDto
  ): SequencingRunDetail {
    return {
      runId: response.runId,
      runCode: response.runCode,
      runName: response.runName ?? null,
      equipment: response.equipment,
      version: response.version,
      indexType: response.indexType,
      cellIdentifier: response.cellIdentifier,
      cellLength: response.cellLength,
      cellFilter: response.cellFilter,
      technologyType: response.technologyType ?? 'NGS',
      status: response.status,
      createdByUserId: response.createdByUserId,
      createdAt: response.createdAt,
      updatedAt: response.updatedAt,
      // Use Factory to create technology-specific library assignments (OCP compliant)
      libraryAssignments: RunLibraryAssignmentFactory.createList(
        response.libraryAssignments
      )
    };
  }

  /**
   * Map backend list response to domain model
   */
  private mapToListResponse(
    response: SequencingRunListBackendResponse
  ): SequencingRunListResponse {
    return {
      content: response.content.map((item) => this.mapToListItem(item)),
      pageable: {
        pageNumber: response.pageable.page,
        pageSize: response.pageable.size,
        totalElements: response.pageable.totalElements,
        totalPages: response.pageable.totalPages,
        last: response.pageable.last,
        first: response.pageable.first
      }
    };
  }

  /**
   * Map backend list item to domain model
   */
  private mapToListItem(
    item: SequencingRunListItemBackendDto
  ): SequencingRunListItem {
    return {
      runId: item.runId,
      runCode: item.runCode,
      runName: item.runName,
      equipment: item.equipment,
      status: item.status,
      technologyType: item.technologyType,
      createdAt: item.createdAt,
      createdBy: {
        userId: item.createdBy.userId,
        fullName: item.createdBy.fullName
      },
      assignedLibrariesCount: item.assignedLibrariesCount
    };
  }

  markRunAsFailed(
    request: MarkRunAsFailedRequest
  ): Observable<MarkRunAsFailedResponse> {
    const backendRequest: MarkRunAsFailedBackendRequest = {
      run_id: request.runId,
      cause_id: request.causeId,
      status_id: request.statusId,
      observations: request.observations,
      is_reprocess: request.isReprocess
    };

    return this.httpClient
      .post<MarkRunAsFailedBackendResponse>(
        `${this.baseUrl}/runs/mark-as-failed`,
        backendRequest
      )
      .pipe(
        map((response) => ({
          runId: response.run_id,
          status: response.status,
          message: response.message
        }))
      );
  }

  override getRunSpecimens(runId: string): Observable<RunSpecimen[]> {
    return this.httpClient
      .get<
        RunSpecimenBackendDto[]
      >(`${this.baseUrl}/sequencing-runs/${runId}/specimens`)
      .pipe(
        map((response) =>
          response.map((item) => ({
            specimenId: item.specimenId,
            specimenCode: item.specimenCode,
            lgCode: item.lgCode,
            patientName: item.patientName,
            examCode: item.examCode,
            libraryCode: item.libraryCode,
            inReprocess: item.inReprocess ?? false,
            testCode: item.testCode ?? '',
            sentToAnalysis: item.sentToAnalysis ?? false
          }))
        )
      );
  }

  override markRunAsSuccess(
    request: MarkRunAsSuccessRequest
  ): Observable<MarkRunAsSuccessResponse> {
    const backendRequest: MarkRunAsSuccessBackendRequest = {
      runId: request.runId,
      causeId: request.causeId,
      statusId: request.statusId,
      specimenIds: request.specimenIds,
      observations: request.observations,
      ...(request.specimenSelections !== undefined && { specimenSelections: request.specimenSelections })
    };

    return this.httpClient
      .post<MarkRunAsSuccessBackendResponse>(
        `${this.baseUrl}/runs/mark-as-success`,
        backendRequest
      )
      .pipe(
        map((response) => ({
          success: response.success,
          specimensProcessed: response.specimensProcessed,
          message: response.message
        }))
      );
  }

  override updateRunStatus(runId: string, status: string): Observable<void> {
    return this.httpClient.patch<void>(
      `${this.baseUrl}/sequencing-runs/${runId}/status`,
      { status }
    );
  }

  override sendToAnalysis(
    runId: string,
    specimenIds: string[] | null,
    specimenSelections?: { specimenId: string; testCode: string }[]
  ): Observable<SendToAnalysisResponse> {
    return this.httpClient
      .post<SendToAnalysisResponse>(
        `${this.baseUrl}/runs/${runId}/send-to-analysis`,
        { specimenIds, ...(specimenSelections ? { specimenSelections } : {}) }
      )
      .pipe(timeout(this.HTTP_TIMEOUT_MS));
  }
}
